import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { getSystemName } from "@/utils/systemNames";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Edit2,
  Trash2,
  RefreshCw,
  Database,
  Loader2,
  Gamepad2,
  Calendar,
  StickyNote,
  Star,
  Archive,
  CheckCircle2,
  CheckSquare,
  Tags,
  Tag,
  MoreVertical,
  Plus,
  X,
  Trophy,
  ChevronDown,
  Download,
  AlertTriangle,
  Clock,
  Link2,
  ExternalLink,
  Bell,
  BellRing,
  BellOff,
  Share2,
  EyeOff,
  ImageIcon,
  ListTodo,
  Copy,
  Ban,
  Upload,
  Award,
  Lock,
  Eye,
  GripVertical,
  ChevronRight,
  Bold,
  Italic,
  Grid3X3,
  FileText,
  MapPin,
  Pin,
  ShoppingCart,
} from "lucide-react";
import SystemBarOverlay from "@/components/SystemBarOverlay";
import { RichTextDisplay } from "@/components/ui/rich-text-editor";
import { linkify } from "@/lib/linkify";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ChecklistDisplay, AddNoteDialog, EditChecklistDialog, ShareChecklistDialog } from "./GameDialogs";
import ScorecardEditor from "./ScorecardEditor";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import PlatformBadge, { SystemBadge } from "@/components/PlatformBadge";
import SlopBadge from "@/components/SlopBadge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MapEditor, MapDisplay } from "./MapNote";

const API = `${getApiUrl()}/api`;

const getScoreAbbr = (platform) => {
  const abbrs = {
    xbox: "G",
    playstation: "XP",
    steam: "pts",
    epic: "XP",
  };
  return abbrs[platform] || "pts";
};

// Parse completion estimate string like "120-150 hours" or "20-30 hours"
const parseCompletionEstimate = (estimate) => {
  if (!estimate) return null;
  
  // Match patterns like "120-150 hours", "20-30", "50 hours", "~100 hours"
  const rangeMatch = estimate.match(/(\d+)\s*-\s*(\d+)/);
  const singleMatch = estimate.match(/~?(\d+)\s*(?:hours?)?/i);
  
  if (rangeMatch) {
    const min = parseInt(rangeMatch[1], 10);
    const max = parseInt(rangeMatch[2], 10);
    return { min, max, avg: (min + max) / 2 };
  } else if (singleMatch) {
    const value = parseInt(singleMatch[1], 10);
    return { min: value, max: value, avg: value };
  }
  return null;
};

// Format hours nicely (e.g., 1.5 -> "1.5h", 60 -> "60h")
const formatHours = (hours) => {
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours % 1 === 0) return `${hours}h`;
  return `${hours.toFixed(1)}h`;
};

// Completion Estimate Card Component
const CompletionEstimateCard = ({ completionEstimate, totalAchievements, totalScore, unlockedAchievements, earnedScore, sessionTimeHours, platform, dlcs = [], onEdit, showAddButton }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(completionEstimate || "");
  
  const handleSave = () => {
    if (onEdit) {
      onEdit(editValue.trim() || null);
    }
    setIsEditing(false);
  };
  
  const handleCancel = () => {
    setEditValue(completionEstimate || "");
    setIsEditing(false);
  };
  
  // Parse base game estimate
  const baseParsed = parseCompletionEstimate(completionEstimate);
  
  // Calculate aggregated estimates including started DLCs
  // A DLC is "started" if it has at least 1 unlocked achievement
  const startedDlcs = dlcs.filter(dlc => 
    dlc.unlockedAchievements > 0 && dlc.completionEstimate
  );
  
  // Parse all started DLC estimates
  const dlcEstimates = startedDlcs.map(dlc => ({
    ...parseCompletionEstimate(dlc.completionEstimate),
    name: dlc.name,
    totalAchievements: dlc.totalAchievements || 0,
    unlockedAchievements: dlc.unlockedAchievements || 0,
    totalScore: dlc.totalScore || 0,
    earnedScore: dlc.earnedScore || 0,
  })).filter(e => e.min !== undefined);
  
  // Calculate total estimates (base + started DLCs)
  let totalMin = baseParsed?.min || 0;
  let totalMax = baseParsed?.max || 0;
  let totalAvg = baseParsed?.avg || 0;
  
  // Add DLC times
  dlcEstimates.forEach(dlc => {
    totalMin += dlc.min;
    totalMax += dlc.max;
    totalAvg += dlc.avg;
  });
  
  // Calculate combined achievements/scores (base + started DLCs)
  let combinedTotalAch = totalAchievements;
  let combinedUnlockedAch = unlockedAchievements || 0;
  let combinedTotalScore = totalScore;
  let combinedEarnedScore = earnedScore || 0;
  
  dlcEstimates.forEach(dlc => {
    combinedTotalAch += dlc.totalAchievements;
    combinedUnlockedAch += dlc.unlockedAchievements;
    combinedTotalScore += dlc.totalScore;
    combinedEarnedScore += dlc.earnedScore;
  });
  
  // If no estimates at all, show simple card with edit option or add button
  if (totalMin === 0 && totalMax === 0) {
    if (completionEstimate) {
      return (
        <Card className="bg-[#121212] border-white/5">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-cyan-400/70 text-sm">
                <Clock className="w-4 h-4" />
                Estimated Time
              </div>
              {onEdit && (
                <Popover open={isEditing} onOpenChange={setIsEditing}>
                  <PopoverTrigger asChild>
                    <button className="p-1 hover:bg-white/10 rounded transition-colors" title="Edit estimate">
                      <Edit2 className="w-3.5 h-3.5 text-white/40 hover:text-white/70" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3 bg-[#1a1a1a] border-white/10" align="end">
                    <div className="space-y-3">
                      <Label className="text-white/70 text-xs">Completion Estimate</Label>
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder="e.g., 20-30 hours"
                        className="bg-black/50 border-white/10 h-8 text-sm"
                        onKeyDown={(e) => e.key === "Enter" && handleSave()}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={handleCancel} className="flex-1 h-7 text-xs">
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleSave} className="flex-1 h-7 text-xs bg-cyan-600 hover:bg-cyan-700">
                          Save
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <p className="text-cyan-400 text-lg font-bold mt-1">{completionEstimate}</p>
          </CardContent>
        </Card>
      );
    }
    // Show "Add Estimate" button if showAddButton is true and onEdit exists
    if (showAddButton && onEdit) {
      return (
        <Card className="bg-[#121212] border-white/5 border-dashed">
          <CardContent className="p-3">
            <Popover open={isEditing} onOpenChange={setIsEditing}>
              <PopoverTrigger asChild>
                <button className="w-full flex items-center justify-center gap-2 text-white/40 hover:text-white/60 text-sm py-1 transition-colors">
                  <Plus className="w-4 h-4" />
                  Add Completion Estimate
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3 bg-[#1a1a1a] border-white/10" align="center">
                <div className="space-y-3">
                  <Label className="text-white/70 text-xs">Completion Estimate</Label>
                  <Input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder="e.g., 20-30 hours"
                    className="bg-black/50 border-white/10 h-8 text-sm"
                    onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={handleCancel} className="flex-1 h-7 text-xs">
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSave} className="flex-1 h-7 text-xs bg-cyan-600 hover:bg-cyan-700">
                      Add
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </CardContent>
        </Card>
      );
    }
    return null;
  }
  
  const scoreAbbr = getScoreAbbr(platform);
  
  // Calculate completion percentage based on combined achievements
  const achievementPct = combinedTotalAch > 0 ? combinedUnlockedAch / combinedTotalAch : 0;
  const scorePct = combinedTotalScore > 0 ? combinedEarnedScore / combinedTotalScore : 0;
  const completionPct = achievementPct > 0 ? achievementPct : scorePct;
  
  // Remaining work
  const remainingAchievements = combinedTotalAch - combinedUnlockedAch;
  const remainingScore = combinedTotalScore - combinedEarnedScore;
  const remainingPct = 1 - completionPct;
  
  // Calculate remaining time based on progress
  const remainingMinHours = Math.max(0, Math.round(totalMin * remainingPct * 10) / 10);
  const remainingMaxHours = Math.max(0, Math.round(totalMax * remainingPct * 10) / 10);
  const remainingAvgHours = Math.max(0, Math.round(totalAvg * remainingPct * 10) / 10);
  
  // Calculate rates for REMAINING work (achievements/score per hour)
  const remainingAchPerHrMin = remainingAchievements > 0 && remainingMaxHours > 0 ? (remainingAchievements / remainingMaxHours).toFixed(1) : "0";
  const remainingAchPerHrMax = remainingAchievements > 0 && remainingMinHours > 0 ? (remainingAchievements / remainingMinHours).toFixed(1) : "0";
  
  const remainingScorePerHrMin = remainingScore > 0 && remainingMaxHours > 0 ? Math.round(remainingScore / remainingMaxHours) : 0;
  const remainingScorePerHrMax = remainingScore > 0 && remainingMinHours > 0 ? Math.round(remainingScore / remainingMinHours) : 0;
  
  // Calculate remaining sessions
  const remainingSessionsMin = sessionTimeHours > 0 && remainingMinHours > 0 ? Math.ceil(remainingMinHours / sessionTimeHours) : 0;
  const remainingSessionsMax = sessionTimeHours > 0 && remainingMaxHours > 0 ? Math.ceil(remainingMaxHours / sessionTimeHours) : 0;
  
  // Total time calculations (for reference)
  const totalTimeDisplay = totalMin === totalMax ? `${totalMin}h` : `${totalMin}-${totalMax}h`;
  
  // Remaining time display
  const remainingTimeDisplay = remainingMinHours === remainingMaxHours 
    ? formatHours(remainingAvgHours)
    : `${formatHours(remainingMinHours)}-${formatHours(remainingMaxHours)}`;
  
  // Is game complete?
  const isComplete = completionPct >= 1;
  
  // Count of included DLCs for display
  const includedDlcCount = dlcEstimates.length;
  
  return (
    <Card className="bg-[#121212] border-white/5">
      <CardContent className="p-3">
        {/* Header with total estimate */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-cyan-400/70 text-sm">
            <Clock className="w-4 h-4" />
            Completion Estimate
            {includedDlcCount > 0 && (
              <span className="text-[10px] bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded">
                +{includedDlcCount} DLC{includedDlcCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-white/50">
              Total: {totalTimeDisplay}
            </span>
            {onEdit && (
              <Popover open={isEditing} onOpenChange={setIsEditing}>
                <PopoverTrigger asChild>
                  <button className="p-1 hover:bg-white/10 rounded transition-colors" title="Edit estimate">
                    <Edit2 className="w-3.5 h-3.5 text-white/40 hover:text-white/70" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3 bg-[#1a1a1a] border-white/10" align="end">
                  <div className="space-y-3">
                    <Label className="text-white/70 text-xs">Completion Estimate</Label>
                    <Input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      placeholder="e.g., 20-30 hours"
                      className="bg-black/50 border-white/10 h-8 text-sm"
                      onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={handleCancel} className="flex-1 h-7 text-xs">
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSave} className="flex-1 h-7 text-xs bg-cyan-600 hover:bg-cyan-700">
                        Save
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
        
        {/* Remaining Time - Main Display */}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-white/60 text-sm">Remaining:</span>
          <span className={`text-xl font-bold ${isComplete ? "text-green-400" : "text-cyan-400"}`}>
            {isComplete ? "Complete! 🎉" : remainingTimeDisplay}
          </span>
        </div>
        
        {/* Progress bar */}
        <div className="mt-2">
          <Progress value={completionPct * 100} className="h-1.5 [&>div]:bg-cyan-500" />
          <p className="text-white/40 text-xs mt-1 text-right">{Math.round(completionPct * 100)}% complete</p>
        </div>
        
        {/* Stats Grid - Only show if not complete */}
        {!isComplete && (
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            {/* Remaining Achievements per Hour */}
            <div className="bg-black/30 rounded p-2 text-center">
              <p className="text-white/40 mb-1">Ach/hr left</p>
              <p className="text-green-400 font-semibold">
                {remainingAchPerHrMin === remainingAchPerHrMax 
                  ? remainingAchPerHrMin
                  : `${remainingAchPerHrMin}-${remainingAchPerHrMax}`}
              </p>
              <p className="text-white/30 text-[10px] mt-0.5">{remainingAchievements} left</p>
            </div>
            
            {/* Remaining Score per Hour */}
            <div className="bg-black/30 rounded p-2 text-center">
              <p className="text-white/40 mb-1">{scoreAbbr}/hr left</p>
              <p className="text-yellow-400 font-semibold">
                {remainingScorePerHrMin === remainingScorePerHrMax 
                  ? remainingScorePerHrMin.toLocaleString()
                  : `${remainingScorePerHrMin.toLocaleString()}-${remainingScorePerHrMax.toLocaleString()}`}
              </p>
              <p className="text-white/30 text-[10px] mt-0.5">{remainingScore.toLocaleString()} left</p>
            </div>
            
            {/* Remaining Sessions */}
            <div className="bg-black/30 rounded p-2 text-center">
              <p className="text-white/40 mb-1">Sessions ({sessionTimeHours}h)</p>
              <p className="text-cyan-400 font-semibold">
                {remainingSessionsMin === remainingSessionsMax 
                  ? remainingSessionsMin
                  : `${remainingSessionsMin}-${remainingSessionsMax}`}
              </p>
              <p className="text-white/30 text-[10px] mt-0.5">remaining</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Format duration between two dates
const formatDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return "N/A";
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end - start;
  
  if (diffMs < 0) return "N/A";
  
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (diffDays > 365) {
    const years = Math.floor(diffDays / 365);
    const remainingDays = diffDays % 365;
    return `${years}y ${remainingDays}d`;
  } else if (diffDays > 30) {
    const months = Math.floor(diffDays / 30);
    const remainingDays = diffDays % 30;
    return `${months}mo ${remainingDays}d`;
  } else if (diffDays > 0) {
    return `${diffDays}d ${diffHours}h`;
  } else if (diffHours > 0) {
    return `${diffHours}h ${diffMins}m`;
  } else {
    return `${diffMins}m`;
  }
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return "Unknown";
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

// Completion Stats Card Component (for completed games)
const CompletionStatsCard = ({ achievements, platform }) => {
  // Get unlocked achievements sorted by earnedAt
  const unlockedAchievements = achievements
    ?.filter(a => a.isUnlocked && a.earnedAt)
    ?.sort((a, b) => new Date(a.earnedAt) - new Date(b.earnedAt)) || [];
  
  if (unlockedAchievements.length === 0) {
    return null;
  }
  
  const firstAchievement = unlockedAchievements[0];
  const lastAchievement = unlockedAchievements[unlockedAchievements.length - 1];
  
  // Calculate stats
  const totalDuration = formatDuration(firstAchievement.earnedAt, lastAchievement.earnedAt);
  const totalScore = achievements?.reduce((sum, a) => sum + (a.score || 0), 0) || 0;
  
  // Calculate average time between achievements
  let avgTimeBetween = "N/A";
  if (unlockedAchievements.length > 1) {
    const firstTime = new Date(firstAchievement.earnedAt).getTime();
    const lastTime = new Date(lastAchievement.earnedAt).getTime();
    const avgMs = (lastTime - firstTime) / (unlockedAchievements.length - 1);
    
    const avgHours = avgMs / (1000 * 60 * 60);
    if (avgHours < 1) {
      avgTimeBetween = `${Math.round(avgMs / (1000 * 60))}m`;
    } else if (avgHours < 24) {
      avgTimeBetween = `${avgHours.toFixed(1)}h`;
    } else {
      avgTimeBetween = `${(avgHours / 24).toFixed(1)}d`;
    }
  }
  
  // Find longest gap between consecutive achievements
  let longestGap = 0;
  let longestGapStart = null;
  let longestGapEnd = null;
  for (let i = 1; i < unlockedAchievements.length; i++) {
    const gap = new Date(unlockedAchievements[i].earnedAt) - new Date(unlockedAchievements[i-1].earnedAt);
    if (gap > longestGap) {
      longestGap = gap;
      longestGapStart = unlockedAchievements[i-1];
      longestGapEnd = unlockedAchievements[i];
    }
  }
  const longestGapFormatted = longestGap > 0 ? formatDuration(longestGapStart?.earnedAt, longestGapEnd?.earnedAt) : "N/A";
  
  // Calculate achievements per day (if completion took more than a day)
  const startDate = new Date(firstAchievement.earnedAt);
  const endDate = new Date(lastAchievement.earnedAt);
  const daysDiff = Math.max(1, (endDate - startDate) / (1000 * 60 * 60 * 24));
  const achievementsPerDay = (unlockedAchievements.length / daysDiff).toFixed(1);
  
  const scoreAbbr = getScoreAbbr(platform);
  
  return (
    <Card className="bg-[#121212] border-white/5 col-span-2">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-emerald-400/70 text-sm mb-3">
          <Trophy className="w-4 h-4" />
          <span className="font-medium">Completion Stats</span>
          <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto" />
        </div>
        
        {/* First and Last Achievement */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* First Achievement */}
          <div className="bg-black/30 rounded p-2">
            <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5">First Achievement</p>
            <div className="flex items-center gap-2">
              {firstAchievement.imageUrl ? (
                <img 
                  src={firstAchievement.imageUrl} 
                  alt={firstAchievement.name}
                  className="w-10 h-10 rounded object-contain bg-black/50"
                />
              ) : (
                <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-white/30" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium truncate" title={firstAchievement.name}>
                  {firstAchievement.name}
                </p>
                <p className="text-white/40 text-[10px]">{formatDate(firstAchievement.earnedAt)}</p>
                {firstAchievement.score > 0 && (
                  <p className="text-yellow-400/70 text-[10px]">{firstAchievement.score} {scoreAbbr}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Last Achievement */}
          <div className="bg-black/30 rounded p-2">
            <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5">Final Achievement</p>
            <div className="flex items-center gap-2">
              {lastAchievement.imageUrl ? (
                <img 
                  src={lastAchievement.imageUrl} 
                  alt={lastAchievement.name}
                  className="w-10 h-10 rounded object-contain bg-black/50"
                />
              ) : (
                <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-white/30" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium truncate" title={lastAchievement.name}>
                  {lastAchievement.name}
                </p>
                <p className="text-white/40 text-[10px]">{formatDate(lastAchievement.earnedAt)}</p>
                {lastAchievement.score > 0 && (
                  <p className="text-yellow-400/70 text-[10px]">{lastAchievement.score} {scoreAbbr}</p>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-2">
          <div className="bg-black/30 rounded p-2 text-center">
            <p className="text-white/40 text-[10px] mb-0.5">Total Time</p>
            <p className="text-emerald-400 font-semibold text-sm">{totalDuration}</p>
          </div>
          <div className="bg-black/30 rounded p-2 text-center">
            <p className="text-white/40 text-[10px] mb-0.5">Avg. Between</p>
            <p className="text-emerald-400 font-semibold text-sm">{avgTimeBetween}</p>
          </div>
          <div className="bg-black/30 rounded p-2 text-center">
            <p className="text-white/40 text-[10px] mb-0.5">Longest Gap</p>
            <p className="text-orange-400 font-semibold text-sm">{longestGapFormatted}</p>
          </div>
          <div className="bg-black/30 rounded p-2 text-center">
            <p className="text-white/40 text-[10px] mb-0.5">Per Day</p>
            <p className="text-emerald-400 font-semibold text-sm">{achievementsPerDay}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


/** Render markdown-style bold/italic text */
function RichText({ text }) {
  if (!text) return null;
  // Split on **bold** and *italic* patterns
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("*") && part.endsWith("*")) {
      return <em key={i} className="italic">{part.slice(1, -1)}</em>;
    }
    return <span key={i}>{linkify(part, `gh-${i}`)}</span>;
  });
}

/** SortableItem wrapper for drag-and-drop */
function SortableItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-1 group/sort">
      <button
        {...attributes}
        {...listeners}
        className="mt-2 p-0.5 text-white/30 hover:text-white/70 cursor-grab active:cursor-grabbing shrink-0 transition-colors"
        data-testid={`drag-handle-${id}`}
        title="Drag to reorder"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}


export default function GameHeaderNew({
  game,
  platforms,
  dlcs,
  availableGameTags,
  achievements,
  groupedAchievements,
  sessionTimeHours = 2,
  masterGenres = [],
  // Handlers
  onToggleOwned,
  onToggleNoAchievements,
  onTogglePriority,
  onToggleBacklog,
  onToggleHidden,
  onToggleManualComplete,
  onToggleIncompleteSync,
  onResolveIncompleteSync,
  onRemoveCopyNumber,
  onEditInfo,
  onRefreshMetadata,
  onGetCoverArt,
  onSyncAchievements,
  onManualSync,
  onToggleSyncDisabled,
  onExophaseVerify,
  chatlogImportEnabled = false,
  onOpenChatlogImport,
  onDelete,
  onEditTags,
  onAddGameTag,
  onRemoveGameTag,
  onAddNote,
  onEditNote,
  onDeleteNote,
  onToggleChecklistItem,
  onShareChecklist,
  onEditChecklist,
  onAddChecklist,
  onReorderNotes,
  checklistTemplates = [],
  onAddLink,
  onEditLink,
  onDeleteLink,
  onReorderLinks,
  onReorderContent,
  // Map handlers
  onAddMap,
  onEditMap,
  onToggleMapPin,
  onSubmitMapToCommunity,
  onToggleNotePlanner,
  onUpdateCompletionEstimate,
  onLinkPlayStation,
  // System name lookup
  systemsLookup = {},
  // Loading states
  togglingPriority,
  syncing,
  refreshLoading,
  canSync,
}) {
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [scorecardsExpanded, setScorecardsExpanded] = useState(false);
  const [linksExpanded, setLinksExpanded] = useState(true);
  const [remindersExpanded, setRemindersExpanded] = useState(true);
  const [gameTagsPopoverOpen, setGameTagsPopoverOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [shareChecklist, setShareChecklist] = useState(null);
  
  // Helper to convert genre slug to display name
  const getGenreDisplayName = (slug) => {
    const genre = masterGenres.find(g => g.slug === slug);
    return genre ? genre.name : slug;
  };
  
  // Links state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [newLinkTitle, setNewLinkTitle] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [newLinkDescription, setNewLinkDescription] = useState("");
  const [editingLink, setEditingLink] = useState(null);
  const [linkIsPrivate, setLinkIsPrivate] = useState(false);
  const [expandedNoteIds, setExpandedNoteIds] = useState(new Set());
  const [initialNoteType, setInitialNoteType] = useState("note");
  const [mapEditorOpen, setMapEditorOpen] = useState(false);
  const [editingMap, setEditingMap] = useState(null);

  // Scorecard/Grid state (lifted from ScorecardPanel)
  const [scorecards, setScorecards] = useState([]);
  const [expandedGrids, setExpandedGrids] = useState({});
  // Per-link expand state — used to show the URL row beneath the link
  // header so the link card visually matches notes/maps/grids/checklists.
  const [expandedLinks, setExpandedLinks] = useState({});
  const [showNewGridForm, setShowNewGridForm] = useState(false);
  const [newGridName, setNewGridName] = useState("");
  const [gridSaving, setGridSaving] = useState(false);

  // DnD sensors - must be at top level
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  
  // Notifications/Reminders state
  const [gameNotifications, setGameNotifications] = useState([]);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    name: "",
    scheduleType: "once",
    scheduledDate: "",
    scheduledTime: "09:00",
  });
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    notes: true,
    links: true,
    reminders: true,
    challenges: true,
    sections: true,
  });
  
  // Game Following state (for master DB games)
  const [isFollowing, setIsFollowing] = useState(false);
  const [followSettings, setFollowSettings] = useState({
    notifyTrivia: true,
    notifyChallenges: true,
    notifyOrganizers: true
  });
  const [followLoading, setFollowLoading] = useState(false);
  const [communityAlerts, setCommunityAlerts] = useState(true);
  const [communityAlertsLoading, setCommunityAlertsLoading] = useState(false);

  // Community Items badge alerts (auto-on for library games; opt-out per game)
  useEffect(() => {
    if (!game?.masterGameId) return;
    axios.get(`${API}/community/library-follow/${game.masterGameId}`)
      .then((res) => setCommunityAlerts(res.data.following))
      .catch(() => {});
  }, [game?.masterGameId]);

  const handleToggleCommunityAlerts = async () => {
    if (!game?.masterGameId) return;
    setCommunityAlertsLoading(true);
    try {
      const res = await axios.post(`${API}/community/library-follow/${game.masterGameId}/toggle`);
      setCommunityAlerts(res.data.following);
      window.dispatchEvent(new Event("community-unread-refresh"));
      toast.success(res.data.following ? "Community Items alerts on for this game" : "Community Items alerts off for this game");
    } catch (e) {
      toast.error("Failed to update alerts");
    } finally {
      setCommunityAlertsLoading(false);
    }
  };
  
  // Fetch follow status for master DB games
  useEffect(() => {
    const fetchFollowStatus = async () => {
      if (!game?.masterGameId) return;
      try {
        const response = await axios.get(`${API}/community/following/${game.masterGameId}`);
        setIsFollowing(response.data.following);
        if (response.data.settings) {
          setFollowSettings({
            notifyTrivia: response.data.settings.notifyTrivia,
            notifyChallenges: response.data.settings.notifyChallenges,
            notifyOrganizers: response.data.settings.notifyOrganizers
          });
        }
      } catch (error) {
        console.error("Error fetching follow status:", error);
      }
    };
    fetchFollowStatus();
  }, [game?.masterGameId]);
  
  const handleFollowGame = async () => {
    if (!game?.masterGameId) return;
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await axios.delete(`${API}/community/following/${game.masterGameId}`);
        setIsFollowing(false);
        toast.success("Unfollowed game");
      } else {
        await axios.post(`${API}/community/following/${game.masterGameId}`, null, {
          params: followSettings
        });
        setIsFollowing(true);
        toast.success("Following game! You'll be notified of new content.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update follow status");
    } finally {
      setFollowLoading(false);
    }
  };

  // Tags that exclude achievements from completion calculations
  // ALWAYS_EXCLUDE_TAGS: Always excluded regardless of unlock status
  // CONDITIONAL_EXCLUDE_TAGS: Only excluded if achievement is NOT unlocked
  const ALWAYS_EXCLUDE_TAGS = ["unobtainable"];
  const CONDITIONAL_EXCLUDE_TAGS = ["partly-discontinued"];
  
  // Calculate stats with exclusion logic
  const totalAchievements = achievements?.length || 0;
  
  // An achievement is excluded if:
  // - It has ALWAYS_EXCLUDE_TAGS, OR
  // - It has CONDITIONAL_EXCLUDE_TAGS AND is NOT unlocked
  const excludedAchievements = achievements?.filter(a => {
    const hasAlwaysExclude = a.tags?.some(t => ALWAYS_EXCLUDE_TAGS.includes(t));
    const hasConditionalExclude = a.tags?.some(t => CONDITIONAL_EXCLUDE_TAGS.includes(t));
    return hasAlwaysExclude || (hasConditionalExclude && !a.isUnlocked);
  })?.length || 0;
  const effectiveTotalAchievements = totalAchievements - excludedAchievements;
  
  const unlockedAchievements = achievements?.filter(a => a.isUnlocked)?.length || 0;
  // Effective unlocked: unlocked achievements that don't have ALWAYS_EXCLUDE_TAGS
  // (partly-discontinued unlocked achievements DO count)
  const effectiveUnlockedAchievements = achievements?.filter(a => 
    a.isUnlocked && !a.tags?.some(t => ALWAYS_EXCLUDE_TAGS.includes(t))
  )?.length || 0;
  
  // Use effective stats for completion percentage
  const completionPct = effectiveTotalAchievements > 0 ? Math.round((effectiveUnlockedAchievements / effectiveTotalAchievements) * 100) : 0;
  
  const totalScore = achievements?.reduce((sum, a) => sum + (a.score || 0), 0) || 0;
  const earnedScore = achievements?.filter(a => a.isUnlocked)?.reduce((sum, a) => sum + (a.score || 0), 0) || 0;
  const scorePct = totalScore > 0 ? Math.round((earnedScore / totalScore) * 100) : 0;

  const platform = platforms?.find(p => p.id === game?.platform);
  const notes = useMemo(() => game?.notes || [], [game?.notes]);
  // Local optimistic copy of `game.links` — kept in sync with the prop and
  // updated immediately on drag-drop so the new order doesn't flicker back
  // while the parent waits for its refetch to complete.
  const [links, setLinks] = useState(game?.links || []);
  useEffect(() => {
    setLinks(game?.links || []);
  }, [game?.links]);

  const handleSaveNote = (noteText, noteTitle, isPrivate) => {
    if (editingNote) {
      onEditNote(editingNote.id, noteText || newNoteText, noteTitle, isPrivate);
    } else {
      onAddNote(noteText || newNoteText, noteTitle, isPrivate);
    }
    setNoteDialogOpen(false);
    setNewNoteText("");
    setEditingNote(null);
  };

  const openEditNote = (note) => {
    setEditingNote(note);
    setNewNoteText(note.text);
    setNoteDialogOpen(true);
  };

  const handleSaveLink = () => {
    if (editingLink) {
      onEditLink(editingLink.id, newLinkTitle, newLinkUrl, linkIsPrivate, newLinkDescription);
    } else {
      onAddLink(newLinkTitle, newLinkUrl, linkIsPrivate, newLinkDescription);
    }
    setLinkDialogOpen(false);
    setNewLinkTitle("");
    setNewLinkUrl("");
    setNewLinkDescription("");
    setLinkIsPrivate(false);
    setEditingLink(null);
  };

  const openEditLink = (link) => {
    setEditingLink(link);
    setNewLinkTitle(link.title);
    setNewLinkUrl(link.url);
    setNewLinkDescription(link.description || "");
    setLinkIsPrivate(link.isPrivate || false);
    setLinkDialogOpen(true);
  };

  // Scorecard/Grid CRUD
  const gameIdForGrids = game?.gameId || game?.id;
  
  const fetchScorecards = useCallback(async () => {
    if (!gameIdForGrids) return;
    try {
      const res = await axios.get(`${API}/scorecards/game/${gameIdForGrids}`);
      setScorecards(res.data);
    } catch (err) {
      console.error("Failed to fetch scorecards:", err);
    }
  }, [gameIdForGrids]);

  useEffect(() => { fetchScorecards(); }, [fetchScorecards]);

  const createGrid = async () => {
    setGridSaving(true);
    try {
      const res = await axios.post(`${API}/scorecards`, {
        name: newGridName || "Grid",
        gameId: gameIdForGrids,
      });
      setScorecards(prev => [...prev, res.data]);
      setExpandedGrids(prev => ({ ...prev, [res.data.id]: true }));
      setShowNewGridForm(false);
      setNewGridName("");
      toast.success("Grid created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create grid");
    } finally {
      setGridSaving(false);
    }
  };

  const saveGrid = async (scorecardId, data) => {
    try {
      const res = await axios.put(`${API}/scorecards/${scorecardId}`, data);
      setScorecards(prev => prev.map(s => s.id === scorecardId ? res.data : s));
      toast.success("Grid saved");
    } catch {
      toast.error("Failed to save grid");
    }
  };

  const deleteGrid = async (scorecardId, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await axios.delete(`${API}/scorecards/${scorecardId}`);
      setScorecards(prev => prev.filter(s => s.id !== scorecardId));
      toast.success("Grid deleted");
    } catch {
      toast.error("Failed to delete grid");
    }
  };

  // Fetch notifications for this game
  const fetchGameNotifications = async () => {
    if (!game?.id) return;
    setLoadingNotifications(true);
    try {
      const response = await axios.get(`${API}/notifications?game_id=${game.id}`);
      setGameNotifications(response.data);
    } catch (error) {
      // Silently fail for notifications - not critical
      console.error("Failed to fetch notifications:", error);
      setGameNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Create notification for this game
  const handleCreateNotification = async () => {
    if (!notificationForm.name.trim()) {
      const { toast } = await import("sonner");
      toast.error("Please enter a name for the reminder");
      return;
    }
    
    try {
      await axios.post(`${API}/notifications`, {
        ...notificationForm,
        gameId: game.id,
        linkedItemName: game.name,
        linkedItemImage: game.boxArtUrl,
        linkedItemType: "game",
      });
      const { toast } = await import("sonner");
      toast.success("Reminder created");
      setNotificationDialogOpen(false);
      setNotificationForm({
        name: "",
        scheduleType: "once",
        scheduledDate: "",
        scheduledTime: "09:00",
      });
      fetchGameNotifications();
    } catch (error) {
      console.error("Failed to create notification:", error);
      const { toast } = await import("sonner");
      if (error.response?.status === 429) {
        toast.error("Too many requests. Please wait a moment.");
      } else {
        toast.error("Failed to create reminder");
      }
    }
  };

  // Delete notification
  const handleDeleteNotification = async (notifId) => {
    try {
      await axios.delete(`${API}/notifications/${notifId}`);
      const { toast } = await import("sonner");
      toast.success("Reminder deleted");
      fetchGameNotifications();
    } catch (error) {
      console.error("Failed to delete notification:", error);
      const { toast } = await import("sonner");
      if (error.response?.status === 429) {
        toast.error("Too many requests. Please wait a moment.");
      } else {
        toast.error("Failed to delete reminder");
      }
    }
  };

  // Load notifications on mount - only once
  useEffect(() => {
    let mounted = true;
    if (game?.id && mounted) {
      fetchGameNotifications();
    }
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.id]);

  const handleExportGame = async () => {
    try {
      const API = getApiUrl();
      const response = await fetch(`${API}/games/${game.id}/export`);
      if (!response.ok) throw new Error("Export failed");
      
      let data = await response.json();
      
      // Filter based on export options
      if (!exportOptions.notes) delete data.notes;
      if (!exportOptions.links) delete data.links;
      if (!exportOptions.reminders) delete data.reminders;
      if (!exportOptions.challenges) delete data.challenges;
      if (!exportOptions.sections) delete data.sections;
      
      const jsonString = JSON.stringify(data, null, 2);
      
      // Create downloadable file
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Sanitize filename
      const safeName = game.name.replace(/[^a-z0-9]/gi, "_").toLowerCase();
      a.download = `${safeName}_config.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      // Also copy to clipboard as backup
      try {
        await navigator.clipboard.writeText(jsonString);
        // Import toast dynamically to avoid adding to imports
        const { toast } = await import("sonner");
        toast.success(`Exported! File downloading... (also copied to clipboard)`);
      } catch {
        const { toast } = await import("sonner");
        toast.success(`Exported! Check your Downloads folder for ${safeName}_config.json`);
      }
      
      setExportDialogOpen(false);
    } catch (error) {
      console.error("Export failed:", error);
      const { toast } = await import("sonner");
      toast.error("Export failed");
    }
  };

  // Build a single mixed-type array combining notes, grids (scorecards), and
  // links, ordered by the game's `contentOrder` array when present. Any items
  // not in `contentOrder` keep their original per-collection order and slot in
  // after the explicitly-ordered ones (matching how the backend reorder
  // endpoints behave). Each item is tagged with `__kind` so the renderer can
  // pick the right card component.
  const combinedItems = useMemo(() => {
    const taggedNotes = (notes || []).map(n => ({ ...n, __kind: "note", __id: n.id }));
    const taggedGrids = (scorecards || []).map(s => ({ ...s, __kind: "grid", __id: s.id }));
    const taggedLinks = (links || []).map(l => ({ ...l, __kind: "link", __id: l.id }));
    const all = [...taggedNotes, ...taggedGrids, ...taggedLinks];
    const order = Array.isArray(game?.contentOrder) ? game.contentOrder : [];
    if (order.length === 0) return all;
    const byId = new Map(all.map(it => [it.__id, it]));
    const ordered = [];
    const seen = new Set();
    for (const id of order) {
      const item = byId.get(id);
      if (item) {
        ordered.push(item);
        seen.add(id);
      }
    }
    for (const it of all) {
      if (!seen.has(it.__id)) ordered.push(it);
    }
    return ordered;
  }, [notes, scorecards, links, game?.contentOrder]);

  // Local optimistic copy of the combined order so dragging feels instant
  // while the backend persists. Items not in `optimisticOrder` fall back to
  // their `combinedItems` order, and stale IDs are filtered out, so no manual
  // reset is needed when source arrays change.
  const [optimisticOrder, setOptimisticOrder] = useState(null);

  const displayItems = useMemo(() => {
    if (!optimisticOrder) return combinedItems;
    const byId = new Map(combinedItems.map(it => [it.__id, it]));
    const ordered = optimisticOrder.map(id => byId.get(id)).filter(Boolean);
    // Append any items that arrived after the optimistic update
    for (const it of combinedItems) {
      if (!optimisticOrder.includes(it.__id)) ordered.push(it);
    }
    return ordered;
  }, [combinedItems, optimisticOrder]);

  const handleCombinedDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = displayItems.map(it => it.__id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(ids, oldIndex, newIndex);
    setOptimisticOrder(newOrder);
    onReorderContent?.(newOrder);
  };

  return (
    <div className="space-y-4 overflow-hidden">
      {/* Main Header Row */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Column: Box Art + Info + Progress */}
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Box Art + Shop button — both live in the same column under
                the image so the Shop link sits directly below the cover art
                instead of crowding the metadata column to the right. */}
            <div className="w-32 sm:w-40 shrink-0 mx-auto sm:mx-0 flex flex-col gap-2">
              <div className="bg-[#121212] rounded-lg overflow-hidden">
                <div className="flex flex-col">
                  {game?.showSystemBar && game?.boxArtUrl && (
                    <SystemBarOverlay
                      platform={game.platform || game.platforms?.[0]}
                      system={game.system || game.systems?.[0]}
                      override={game.systemBarOverride}
                      show={true}
                    />
                  )}
                  <div className="h-44 sm:h-56">
                    {game?.boxArtUrl ? (
                      <img
                        src={game.boxArtUrl}
                        alt={game.name}
                        className="w-full h-full object-contain bg-black/50"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Gamepad2 className="w-12 h-12 text-white/10" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {game?.masterGameId ? (
                <Link
                  to={`/app/browse/${game.masterGameId}`}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-xs font-medium bg-[#121212] hover:bg-cyan-500/15 border border-white/10 hover:border-cyan-400/40 text-white/70 hover:text-cyan-300 transition-all"
                  data-testid="shop-game-link"
                  title="View store links, related games, and where to buy this game"
                >
                  <ShoppingCart className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="text-center leading-tight break-words">Shop {game?.name} and Related</span>
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={onRefreshMetadata}
                  disabled={refreshLoading}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-xs font-medium bg-[#121212] hover:bg-purple-500/15 border border-white/10 hover:border-purple-400/40 text-white/70 hover:text-purple-300 transition-all disabled:opacity-40"
                  data-testid="find-game-in-db-link"
                  title="This game isn't linked to the database yet — search for it to link store links and related games"
                >
                  <Database className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="text-center leading-tight break-words">Find Game in Database</span>
                </button>
              )}
            </div>

            {/* Game Info */}
            <div className="flex-1 min-w-0">
              {/* Title Row with Actions Menu */}
              <div className="flex items-start justify-between gap-2">
                <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white break-words">
                  {game?.name}
                </h1>
                
                {/* Actions Dropdown Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0 text-white/50 hover:text-white shrink-0"
                      data-testid="game-actions-menu"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 sm:w-64 bg-[#0A0A0A] border-white/10">
                    {/* Status Toggles */}
                    <DropdownMenuItem 
                      onClick={onTogglePriority}
                      disabled={togglingPriority}
                      className="cursor-pointer"
                    >
                      <Star className={`w-4 h-4 mr-2 ${game?.isPriority ? "fill-yellow-400 text-yellow-400" : ""}`} />
                      {game?.isPriority ? "Remove from Priority" : "Add to Priority"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onToggleBacklog} className="cursor-pointer">
                      <Archive className={`w-4 h-4 mr-2 ${game?.isBacklog ? "fill-slate-400 text-slate-400" : ""}`} />
                      {game?.isBacklog ? "Remove from Backlog" : "Add to Backlog"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onToggleHidden} className="cursor-pointer">
                      <EyeOff className={`w-4 h-4 mr-2 ${game?.isHidden ? "text-gray-400" : ""}`} />
                      {game?.isHidden ? "Unhide Game" : "Hide Game"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onToggleManualComplete} className="cursor-pointer">
                      <CheckCircle2 className={`w-4 h-4 mr-2 ${game?.isManuallyComplete ? "fill-green-400 text-green-400" : ""}`} />
                      {game?.isManuallyComplete ? "Unmark Complete" : "Mark as Complete"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.preventDefault(); onToggleIncompleteSync?.(); }} className="cursor-pointer">
                      <AlertTriangle className={`w-4 h-4 mr-2 ${game?.isIncompleteSync ? "text-orange-400" : ""}`} />
                      <span className={game?.isIncompleteSync ? "text-orange-400" : ""}>
                        {game?.isIncompleteSync ? "Remove Incomplete Sync" : "Mark Incomplete Sync"}
                      </span>
                    </DropdownMenuItem>
                    {game?.copyNumber && (
                      <DropdownMenuItem onClick={(e) => { e.preventDefault(); onRemoveCopyNumber?.(); }} className="cursor-pointer">
                        <Copy className="w-4 h-4 mr-2 text-blue-400" />
                        <span className="text-blue-400">
                          Remove Copy #{game.copyNumber} Label
                        </span>
                      </DropdownMenuItem>
                    )}
                    
                    <DropdownMenuSeparator className="bg-white/10" />
                    
                    {/* Game Info Checkboxes */}
                    <DropdownMenuItem onClick={(e) => { e.preventDefault(); onToggleOwned(); }} className="cursor-pointer">
                      <Checkbox 
                        checked={game?.isOwned !== false} 
                        className="mr-2 pointer-events-none"
                      />
                      Owned
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator className="bg-white/10" />
                    
                    {/* Actions */}
                    <DropdownMenuItem onClick={onEditInfo} className="cursor-pointer">
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit Game Info
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onRefreshMetadata} disabled={refreshLoading} className="cursor-pointer">
                      <Database className="w-4 h-4 mr-2" />
                      Update from Database
                    </DropdownMenuItem>
                    {onGetCoverArt && (
                      <DropdownMenuItem onClick={onGetCoverArt} className="cursor-pointer">
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Get Cover Art
                      </DropdownMenuItem>
                    )}
                    {chatlogImportEnabled && (
                      <DropdownMenuItem
                        onClick={onOpenChatlogImport}
                        className="cursor-pointer"
                        data-testid="menu-upload-chatlog"
                      >
                        <Upload className="w-4 h-4 mr-2 text-cyan-400" />
                        Upload Homecoming Chat Log
                      </DropdownMenuItem>
                    )}
                    {game?.isAchievementSlop && (
                      <DropdownMenuItem disabled className="cursor-default opacity-60">
                        <Ban className="w-4 h-4 mr-2 text-amber-400" />
                        <span className="text-amber-400 text-xs">Sync disabled — Achievement Slop</span>
                      </DropdownMenuItem>
                    )}
                    {canSync && !game?.isAchievementSlop && !chatlogImportEnabled && (
                      <>
                        <DropdownMenuItem onClick={onSyncAchievements} disabled={syncing} className="cursor-pointer">
                          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
                          Auto Sync from {game?.platform === "playstation" ? "PSN" : game?.platform === "steam" ? "Steam" : "Xbox"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={onManualSync} className="cursor-pointer">
                          <CheckSquare className="w-4 h-4 mr-2" />
                          Manual Bulk Entry
                        </DropdownMenuItem>
                      </>
                    )}
                    {!canSync && game?.syncDisabled && game?.platform === "xbox" && !game?.isAchievementSlop && (
                      <>
                        <DropdownMenuItem disabled className="cursor-default opacity-60">
                          <Ban className="w-4 h-4 mr-2 text-orange-400" />
                          <span className="text-orange-400 text-xs">Sync disabled — API no longer supports this title</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={onToggleSyncDisabled} className="cursor-pointer">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Re-enable Sync
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={onManualSync} className="cursor-pointer">
                          <CheckSquare className="w-4 h-4 mr-2" />
                          Manual Bulk Entry
                        </DropdownMenuItem>
                      </>
                    )}
                    {!canSync && !game?.syncDisabled && onManualSync && (
                      <DropdownMenuItem onClick={onManualSync} className="cursor-pointer">
                        <CheckSquare className="w-4 h-4 mr-2" />
                        Manual Bulk Entry
                      </DropdownMenuItem>
                    )}
                    {game?.platform === "playstation" && !game?.npCommunicationId && onLinkPlayStation && (
                      <DropdownMenuItem onClick={onLinkPlayStation} className="cursor-pointer">
                        <Link2 className="w-4 h-4 mr-2" />
                        Link PlayStation Title
                      </DropdownMenuItem>
                    )}
                    {onExophaseVerify && (
                      <DropdownMenuItem onClick={onExophaseVerify} className="cursor-pointer">
                        <Award className="w-4 h-4 mr-2 text-amber-400" />
                        Verify via Exophase
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => setExportDialogOpen(true)} className="cursor-pointer">
                      <Share2 className="w-4 h-4 mr-2" />
                      Export & Share
                    </DropdownMenuItem>
                    
                    {/* Follow Game for notifications (only for master DB games) */}
                    {game?.masterGameId && (
                      <>
                        <DropdownMenuSeparator className="bg-white/10" />
                        <DropdownMenuItem 
                          onClick={handleFollowGame}
                          disabled={followLoading}
                          className="cursor-pointer"
                        >
                          {isFollowing ? (
                            <>
                              <BellOff className="w-4 h-4 mr-2 text-white/60" />
                              Unfollow Game
                            </>
                          ) : (
                            <>
                              <BellRing className="w-4 h-4 mr-2 text-green-400" />
                              Follow for Updates
                            </>
                          )}
                        </DropdownMenuItem>
                        {isFollowing && (
                          <div className="px-2 py-1 text-xs text-white/40">
                            Get notified about new trivia, challenges & organizers
                          </div>
                        )}
                        <DropdownMenuItem
                          onClick={handleToggleCommunityAlerts}
                          disabled={communityAlertsLoading}
                          className="cursor-pointer"
                          data-testid="toggle-community-alerts"
                        >
                          {communityAlerts ? (
                            <>
                              <Bell className="w-4 h-4 mr-2 text-purple-400" />
                              Community Items: On
                            </>
                          ) : (
                            <>
                              <BellOff className="w-4 h-4 mr-2 text-white/60" />
                              Community Items: Off
                            </>
                          )}
                        </DropdownMenuItem>
                        <div className="px-2 py-1 text-xs text-white/40">
                          {communityAlerts
                            ? "New community grids, maps, trivia & more for this game show in the Community Items badge"
                            : "This game won't add to the Community Items badge"}
                        </div>
                      </>
                    )}
                    
                    <DropdownMenuSeparator className="bg-white/10" />
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <DropdownMenuItem 
                          data-testid="delete-game-menu-item"
                          onSelect={(e) => e.preventDefault()}
                          className="cursor-pointer text-red-400 focus:text-red-400"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Game
                        </DropdownMenuItem>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Delete Game?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete &quot;{game?.name}&quot; and all its achievements. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel 
                            data-testid="delete-game-cancel-btn"
                            className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                          >
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            data-testid="delete-game-confirm-btn"
                            onClick={onDelete}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Platform & System */}
              <div className="flex flex-col gap-1 mt-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {game?.platform && <PlatformBadge platform={game.platform} />}
                  {game?.isAchievementSlop && <SlopBadge />}
                </div>
                {/* Playable on: primary system first, then any additional systems */}
                {(() => {
                  const primary = game?.system;
                  const allSystems = Array.isArray(game?.systems) ? game.systems.filter(Boolean) : [];
                  // Build ordered list: primary first (if known), then the rest of `systems`.
                  // De-duplication needs to handle slug↔display-name pairs because the
                  // `system` field often stores a slug (e.g. "xbox-one") while
                  // `systems[]` stores display names (e.g. "Xbox One"). Resolve every
                  // entry to its display name first, then key by stripped-alphanumeric
                  // lowercase form so "xbox-one" and "Xbox One" collapse to one row.
                  const seen = new Set();
                  const ordered = [];
                  const push = (raw) => {
                    if (!raw) return;
                    const display = getSystemName(raw, systemsLookup) || String(raw);
                    const key = display.replace(/[^a-z0-9]/gi, "").toLowerCase();
                    if (!key || seen.has(key)) return;
                    seen.add(key);
                    ordered.push(display);
                  };
                  push(primary);
                  allSystems.forEach(push);
                  if (ordered.length === 0) return null;
                  return (
                    <div className="flex items-center gap-2 flex-wrap text-sm" data-testid="playable-on-row">
                      <span className="text-white/40">Playable on:</span>
                      {ordered.map((sysName, idx) => (
                        <span key={sysName} className="text-white/70">
                          {sysName}{idx < ordered.length - 1 ? "," : ""}
                        </span>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Release Date - on its own line */}
              {game?.releaseDate && (
                <div className="mt-2">
                  <span className="text-white/40 text-sm flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {game.releaseDate}
                  </span>
                </div>
              )}

              {/* Publisher/Developer/Genres/Ratings */}
              <div className="mt-2 space-y-1 text-sm">
                {((game?.publishers?.length > 0) || game?.publisher) && (
                  <p className="text-white/50">
                    <span className="text-white/30">Publisher:</span> {(game.publishers?.length > 0) ? game.publishers.join(", ") : game.publisher}
                  </p>
                )}
                {((game?.developers?.length > 0) || game?.developer) && (
                  <p className="text-white/50">
                    <span className="text-white/30">Developer:</span> {(game.developers?.length > 0) ? game.developers.join(", ") : game.developer}
                  </p>
                )}
                {game?.genres?.length > 0 && (
                  <p className="text-white/50">
                    <span className="text-white/30">Genres:</span> {game.genres.map(g => getGenreDisplayName(g)).join(", ")}
                  </p>
                )}
                {game?.ratings?.length > 0 && (
                  <p className="text-white/50" data-testid="game-ratings-display">
                    <span className="text-white/30">Rating:</span> {game.ratings.join(", ")}
                  </p>
                )}
                {(game?.completionEstimateFast || game?.completionEstimateLong) && (
                  <p className="text-white/50" data-testid="game-completion-range">
                    <span className="text-white/30">Completion:</span>{" "}
                    {game.completionEstimateFast && game.completionEstimateLong
                      ? `${game.completionEstimateFast} - ${game.completionEstimateLong}`
                      : game.completionEstimateFast || game.completionEstimateLong}
                  </p>
                )}
              </div>
              
              {/* Content Tags from Master DB */}
              {game?.contentTags?.length > 0 && (
                <TooltipProvider delayDuration={200}>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {game.contentTags.map((tag, idx) => {
                      const tagName = typeof tag === "string" ? tag : tag.name;
                      const tagDesc = typeof tag === "string" ? "" : (tag.description || "");
                      const badge = (
                        <span
                          className="px-2 py-0.5 rounded text-xs bg-pink-500/20 text-pink-400 border border-pink-500/30"
                        >
                          {tagName}
                        </span>
                      );
                      if (tagDesc) {
                        return (
                          <Tooltip key={idx}>
                            <TooltipTrigger asChild>{badge}</TooltipTrigger>
                            <TooltipContent side="top" className="bg-[#1A1A1A] border-white/10 text-white/80 max-w-xs">
                              <p className="text-sm">{tagDesc}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      }
                      return <span key={idx}>{badge}</span>;
                    })}
                  </div>
                </TooltipProvider>
              )}

              {/* Game Tags (below genres) */}
              {availableGameTags?.length > 0 && (
                <TooltipProvider delayDuration={200}>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Tags className="w-4 h-4 text-white/30" />
                    {(game?.userTags || []).map((tagId) => {
                      const tag = availableGameTags.find(t => t.id === tagId);
                      if (!tag) return null;
                      const badge = (
                        <span
                          key={tag.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80"
                          style={{ backgroundColor: `${tag.color}20`, color: tag.color, border: `1px solid ${tag.color}40` }}
                          onClick={() => onRemoveGameTag(tag.id)}
                        >
                          {tag.name}
                          <X className="w-3 h-3" />
                        </span>
                      );
                      if (tag.description) {
                        return (
                          <Tooltip key={tag.id}>
                            <TooltipTrigger asChild>{badge}</TooltipTrigger>
                            <TooltipContent side="top" className="bg-[#1A1A1A] border-white/10 text-white/80 max-w-xs">
                              <p className="text-sm">{tag.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      }
                      return badge;
                    })}
                  <Popover open={gameTagsPopoverOpen} onOpenChange={setGameTagsPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-white/40 hover:text-white hover:bg-white/10"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Tag
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-2 bg-[#0A0A0A] border-white/10">
                      <div className="space-y-1">
                        {availableGameTags.filter(t => !(game?.userTags || []).includes(t.id)).map((tag) => (
                          <button
                            key={tag.id}
                            onClick={() => {
                              onAddGameTag(tag.id);
                              setGameTagsPopoverOpen(false);
                            }}
                            className="w-full text-left px-2 py-1 rounded hover:bg-white/10 flex items-center gap-2"
                          >
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                            <span className="text-white text-sm">{tag.name}</span>
                          </button>
                        ))}
                        {availableGameTags.filter(t => !(game?.userTags || []).includes(t.id)).length === 0 && (
                          <p className="text-white/40 text-xs text-center py-2">All tags applied</p>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                </TooltipProvider>
              )}

              {/* Status Badges */}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {game?.isPriority && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                    <Star className="w-3 h-3 fill-yellow-400" />
                    Priority
                  </span>
                )}
                {game?.isBacklog && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-500/20 text-slate-300 border border-slate-500/30">
                    <Archive className="w-3 h-3" />
                    Backlog
                  </span>
                )}
                {game?.isManuallyComplete && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400 border border-green-500/30">
                    <CheckCircle2 className="w-3 h-3 fill-green-400" />
                    Complete
                  </span>
                )}
                {game?.isOwned === false && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400 border border-red-500/30">
                    Not Owned
                    {game?.masterGameId && (!game?.delistedStatus || game?.delistedStatus === "none") && (
                      <a
                        href={`/browse/${game.masterGameId}`}
                        className="inline-flex items-center gap-0.5 ml-1 text-[#FF9900] hover:text-[#FFB84D] transition-colors"
                        title="View buying options on Browse page"
                        data-testid="not-owned-browse-link"
                      >
                        — Buy?
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </span>
                )}
                {game?.isIncompleteSync && (
                  <button
                    onClick={onResolveIncompleteSync}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30 hover:bg-orange-500/30 transition-colors cursor-pointer"
                    title="Click to manually resolve incomplete sync"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    Incomplete Sync
                  </button>
                )}
                {game?.syncDisabled && (
                  <button
                    onClick={onToggleSyncDisabled}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 transition-colors cursor-pointer"
                    title={game.syncDisabledReason || "Sync is disabled for this game. Click to re-enable."}
                    data-testid="sync-disabled-badge"
                  >
                    <Ban className="w-3 h-3" />
                    Sync Disabled
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Custom Notes from Master Database — shown between the tag row
              above and the Achievements progress bar below so it reads as
              part of the game's metadata, not buried under stats. */}
          {game?.customNotes && (
            <div className="mt-4 p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-lg" data-testid="custom-notes-display">
              <div className="flex items-center gap-2 mb-2 text-cyan-400/70 text-xs font-medium uppercase tracking-wider">
                <FileText className="w-3.5 h-3.5" />
                Game Notes
              </div>
              <RichTextDisplay content={game.customNotes} />
            </div>
          )}

          {/* Progress Stats - full width within container */}
          <div className="mt-4 space-y-2">
            <Card className="bg-[#121212] border-white/5">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-green-400/70 text-sm">
                    <Trophy className="w-4 h-4" />
                    Achievements
                  </div>
                  <div className="text-lg font-bold text-green-400">
                    {unlockedAchievements}/{totalAchievements}
                    {excludedAchievements > 0 && (
                      <span className="text-xs text-orange-400/70 ml-1" title={`${excludedAchievements} achievement${excludedAchievements > 1 ? 's' : ''} excluded from completion (unobtainable/discontinued)`}>
                        (-{excludedAchievements})
                      </span>
                    )}
                  </div>
                </div>
                <Progress value={completionPct} className="h-2 mt-2 [&>div]:bg-green-500" />
                <p className="text-green-400/50 text-xs mt-1 text-right">
                  {completionPct}% Complete
                  {excludedAchievements > 0 && (
                    <span className="text-orange-400/50 ml-1">
                      (excl. {excludedAchievements} unobtainable)
                    </span>
                  )}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/5">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-yellow-400/70 text-sm">
                    <Trophy className="w-4 h-4" />
                    Score ({getScoreAbbr(game?.platform)})
                  </div>
                  <div className="text-lg font-bold text-yellow-400">
                    {earnedScore.toLocaleString()}/{totalScore.toLocaleString()}
                  </div>
                </div>
                <Progress value={scorePct} className="h-2 mt-2 [&>div]:bg-yellow-500" />
                <p className="text-yellow-400/50 text-xs mt-1 text-right">{scorePct}% Score</p>
              </CardContent>
            </Card>
            
            {/* Completion Stats Card (for completed games) or Estimated Time Card (for in-progress) */}
            {completionPct >= 100 || game?.isManuallyComplete ? (
              <CompletionStatsCard 
                achievements={achievements}
                platform={game?.platform}
              />
            ) : (game?.completionEstimate || game?.completionEstimateFast || game?.completionEstimateLong) ? (
              <CompletionEstimateCard 
                completionEstimate={
                  game.completionEstimateFast && game.completionEstimateLong
                    ? `${game.completionEstimateFast.replace(/\s*hours?\s*/i, '')}-${game.completionEstimateLong.replace(/\s*hours?\s*/i, '')} hours`
                    : game.completionEstimateFast || game.completionEstimateLong || game.completionEstimate
                }
                totalAchievements={effectiveTotalAchievements}
                totalScore={totalScore}
                unlockedAchievements={effectiveUnlockedAchievements}
                earnedScore={earnedScore}
                sessionTimeHours={sessionTimeHours}
                platform={game?.platform}
                dlcs={dlcs}
                onEdit={onUpdateCompletionEstimate}
              />
            ) : (
              // Show "Add Estimate" card when no estimate exists
              <CompletionEstimateCard 
                completionEstimate={null}
                totalAchievements={effectiveTotalAchievements}
                totalScore={totalScore}
                unlockedAchievements={effectiveUnlockedAchievements}
                earnedScore={earnedScore}
                sessionTimeHours={sessionTimeHours}
                platform={game?.platform}
                dlcs={dlcs}
                onEdit={onUpdateCompletionEstimate}
                showAddButton
              />
            )}
          </div>

        </div>
      </div>
      
      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-400" />
              Export & Share
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-white/60 text-sm">
              Select what to include in the export file:
            </p>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox 
                  checked={exportOptions.notes}
                  onCheckedChange={(checked) => setExportOptions({ ...exportOptions, notes: checked })}
                />
                <span className="text-white/80">Notes ({game?.notes?.length || 0})</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox 
                  checked={exportOptions.links}
                  onCheckedChange={(checked) => setExportOptions({ ...exportOptions, links: checked })}
                />
                <span className="text-white/80">Links ({game?.links?.length || 0})</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox 
                  checked={exportOptions.reminders}
                  onCheckedChange={(checked) => setExportOptions({ ...exportOptions, reminders: checked })}
                />
                <span className="text-white/80">Reminders ({gameNotifications?.length || 0})</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox 
                  checked={exportOptions.challenges}
                  onCheckedChange={(checked) => setExportOptions({ ...exportOptions, challenges: checked })}
                />
                <span className="text-white/80">Custom Challenges</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox 
                  checked={exportOptions.sections}
                  onCheckedChange={(checked) => setExportOptions({ ...exportOptions, sections: checked })}
                />
                <span className="text-white/80">Sections</span>
              </label>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setExportDialogOpen(false)}
                className="text-white/60 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleExportGame}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
